import React, { useContext, useReducer, useEffect, useCallback, useRef, ReactNode } from "react";
import { securityMiddleware, SecurityConfig } from "../middleware/security";
import { useAppStore } from "../store";
import { apiService } from "../services/api/apiService";
import { governanceApiService } from "@/lib/api/governance-api";
import { isKeycloakMode } from "../config/environment";
import { buildKeycloakAuthUrl } from "../config/auth";
import {
  authFetchCredentials,
  BOOTSTRAP_AUTH_ME_SHELL_KEY,
  BOOTSTRAP_CONFIG_SHELL_KEY,
  clearBootstrapSessionCache,
  publishSessionChanged,
  SESSION_CHANGED_EVENT,
} from "../config/authStorage";
import {
  clearPolicySnapshotPermissions,
  hasPermission,
  invalidateSession,
  isSessionInvalidationInProgress,
  acknowledgeAuthenticatedSession,
  markPolicySnapshotLoadFailed,
  refreshSessionTokens,
  setPolicySnapshotPermissions,
  type HubPermission,
} from "../lib/auth/permissions";
import { clearFinanceReadCache, clearSharedReadCache } from "../lib/api/client-shared";
import {
  clearDynamicRoutePermissionContract,
  setDynamicRoutePermissionContract,
  type DynamicRoutePermissionContractEntry,
} from "../lib/constants/routes";
import { createSharedContext } from "../lib/utils";
import { clearFrontendConfigCache, seedFrontendConfigCache, type FrontendConfig } from "@/lib/hooks/use-frontend-config";
import { FRONTEND_CONFIG_QUERY_KEY, readActiveTenantQueryScope, tenantScopedQueryKey } from "@/lib/hooks/query-options";
import { getPlatformQueryClient } from "@/lib/providers/query-provider";
import { useTranslation } from "@/lib/hooks/use-translation";

function applyFrontendConfigFromResponse(
  response: Awaited<ReturnType<typeof apiService.getFrontendConfig>>,
): void {
  if (!response.success || response.data === undefined || response.data === null) {
    return;
  }
  const config = response.data as FrontendConfig;
  if (!Array.isArray(config.navigation_items)) {
    if (import.meta.env.DEV) {
      console.info("[SecurityContext] frontend config missing navigation_items");
    }
    return;
  }
  seedFrontendConfigCache(config);
}

function logFrontendConfigWarmFailure(error: unknown): void {
  if (import.meta.env.DEV) {
    console.info("[SecurityContext] frontend config warm failed", error);
  }
}

interface SecurityState {
  isAuthenticated: boolean;
  sessionHydrated: boolean;
  userName: string;
  userRole: string;
  permissions: string[];
  services: Record<string, unknown>[];
  serviceItems: Record<string, unknown>[];
  policySnapshotReady: boolean;
  policySnapshotError: boolean;
  policyMeta: Record<string, unknown> | null;
  securityLevel: "low" | "medium" | "high";
  lastActivity: Date;
  failedAttempts: number;
  isLocked: boolean;
  securityConfig: SecurityConfig;
}

type SecurityAction =
  | { type: "SET_AUTHENTICATED"; payload: boolean }
  | { type: "SET_SESSION_HYDRATED"; payload: boolean }
  | { type: "SET_USER_NAME"; payload: string }
  | { type: "SET_USER_ROLE"; payload: string }
  | { type: "SET_PERMISSIONS"; payload: string[] }
  | { type: "SET_SERVICES"; payload: Record<string, unknown>[] }
  | { type: "SET_SERVICE_ITEMS"; payload: Record<string, unknown>[] }
  | { type: "SET_POLICY_SNAPSHOT_READY"; payload: boolean }
  | { type: "SET_POLICY_SNAPSHOT_ERROR"; payload: boolean }
  | { type: "SET_POLICY_META"; payload: Record<string, unknown> | null }
  | { type: "SET_SECURITY_LEVEL"; payload: "low" | "medium" | "high" }
  | { type: "UPDATE_LAST_ACTIVITY" }
  | { type: "INCREMENT_FAILED_ATTEMPTS" }
  | { type: "RESET_FAILED_ATTEMPTS" }
  | { type: "SET_LOCKED"; payload: boolean }
  | { type: "UPDATE_SECURITY_CONFIG"; payload: Partial<SecurityConfig> };

const initialState: SecurityState = {
  isAuthenticated: false,
  sessionHydrated: false,
  userName: "",
  userRole: "guest",
  permissions: [],
  services: [],
  serviceItems: [],
  policySnapshotReady: false,
  policySnapshotError: false,
  policyMeta: null,
  securityLevel: "medium",
  lastActivity: new Date(),
  failedAttempts: 0,
  isLocked: false,
  securityConfig: securityMiddleware.getSecurityHeaders() as unknown as SecurityConfig,
};

function securityReducer(state: SecurityState, action: SecurityAction): SecurityState {
  switch (action.type) {
    case "SET_AUTHENTICATED":
      return { ...state, isAuthenticated: action.payload };
    case "SET_SESSION_HYDRATED":
      return { ...state, sessionHydrated: action.payload };
    case "SET_USER_NAME":
      return { ...state, userName: action.payload };
    case "SET_USER_ROLE":
      return { ...state, userRole: action.payload };
    case "SET_PERMISSIONS":
      return { ...state, permissions: action.payload };
    case "SET_SERVICES":
      return { ...state, services: action.payload };
    case "SET_SERVICE_ITEMS":
      return { ...state, serviceItems: action.payload };
    case "SET_POLICY_SNAPSHOT_READY":
      return { ...state, policySnapshotReady: action.payload };
    case "SET_POLICY_SNAPSHOT_ERROR":
      return { ...state, policySnapshotError: action.payload };
    case "SET_POLICY_META":
      return { ...state, policyMeta: action.payload };
    case "SET_SECURITY_LEVEL":
      return { ...state, securityLevel: action.payload };
    case "UPDATE_LAST_ACTIVITY":
      return { ...state, lastActivity: new Date() };
    case "INCREMENT_FAILED_ATTEMPTS":
      return { ...state, failedAttempts: state.failedAttempts + 1 };
    case "RESET_FAILED_ATTEMPTS":
      return { ...state, failedAttempts: 0 };
    case "SET_LOCKED":
      return { ...state, isLocked: action.payload };
    case "UPDATE_SECURITY_CONFIG":
      return {
        ...state,
        securityConfig: { ...state.securityConfig, ...action.payload },
      };
    default:
      return state;
  }
}

interface SecurityContextType {
  state: SecurityState;
  dispatch: React.Dispatch<SecurityAction>;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  setAuthFromKeycloak: (accessToken: string) => void;
  refreshPolicySnapshot: (options?: {
    swr?: boolean;
    force?: boolean;
    seedPermissions?: readonly string[];
  }) => Promise<boolean>;
  checkPermission: (permission: string) => boolean;
  touchSessionActivity: () => void;
  updateSecurityLevel: (level: "low" | "medium" | "high") => void;
  validateInput: (input: string) => string;
  generateCSRFToken: () => string;
  validateCSRFToken: (token: string) => boolean;
}

const SecurityContext = createSharedContext<SecurityContextType | undefined>(
  "__9s_security_react_context__",
  undefined,
);

interface PlatformPolicySnapshot {
  permissions?: unknown;
  capabilities?: unknown;
  services?: unknown;
  service_items?: unknown;
  meta?: unknown;
}

interface AuthProfileSnapshot {
  user_id?: unknown;
  username?: unknown;
  email?: unknown;
  role?: unknown;
  permissions?: unknown;
  user?: { id?: unknown; username?: unknown; email?: unknown };
}

function isPolicySnapshotData(data: unknown): data is PlatformPolicySnapshot {
  if (data === null || typeof data !== "object" || Array.isArray(data)) {
    return false;
  }
  const record = data as Record<string, unknown>;
  if (!Object.prototype.hasOwnProperty.call(record, "permissions")) {
    return false;
  }
  return Array.isArray(record.permissions);
}

function normalizedPermissionsFromSnapshot(snapshot: PlatformPolicySnapshot | null): string[] {
  const values = [
    ...(Array.isArray(snapshot?.permissions) ? snapshot.permissions : []),
    ...(Array.isArray(snapshot?.capabilities) ? snapshot.capabilities : []),
  ];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const permission = String(value).trim();
    if (!permission) continue;
    if (seen.has(permission)) continue;
    seen.add(permission);
    result.push(permission);
  }
  return result;
}

function normalizedServicesFromSnapshot(snapshot: PlatformPolicySnapshot | null): Record<string, unknown>[] {
  if (!Array.isArray(snapshot?.services)) {
    return [];
  }
  return snapshot.services.filter((item): item is Record<string, unknown> => {
    if (!item) {
      return false;
    }
    return typeof item === "object" && !Array.isArray(item);
  });
}

function normalizedServiceItemsFromSnapshot(snapshot: PlatformPolicySnapshot | null): Record<string, unknown>[] {
  if (!Array.isArray(snapshot?.service_items)) {
    return [];
  }
  return snapshot.service_items.filter((item): item is Record<string, unknown> => {
    if (!item) {
      return false;
    }
    return typeof item === "object" && !Array.isArray(item);
  });
}

function serviceItemsRouteContractEntries(
  serviceItems: readonly Record<string, unknown>[]
): DynamicRoutePermissionContractEntry[] {
  return serviceItems
    .filter((item) => typeof item.path === "string")
    .map((item) => ({
      path: item.path,
      permission_key: item.permission_key,
      required_permissions: item.required_permissions,
    }));
}

function textField(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim();
}

function firstText(values: unknown[]): string {
  for (const value of values) {
    const text = textField(value);
    if (text) {
      return text;
    }
  }
  return "";
}

// eslint-disable-next-line react-refresh/only-export-components
export const useSecurityContext = (): SecurityContextType => {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error("useSecurityContext must be used within a SecurityProvider");
  }
  return context;
};

interface SecurityProviderProps {
  children: ReactNode;
}

export const SecurityProvider: React.FC<SecurityProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(securityReducer, initialState);
  const { addNotification } = useAppStore();
  const { t } = useTranslation();
  // Ignore stale hydrate outcomes so a boot /me 401 cannot wipe a newer post-login session.
  const hydrateEpochRef = useRef(0);
  // Keep hydrate callback identity stable across SET_AUTHENTICATED so the mount
  // useEffect does not start a second hydrate mid-login and spuriously fail it.
  const isAuthenticatedRef = useRef(state.isAuthenticated);
  isAuthenticatedRef.current = state.isAuthenticated;

  const logout = useCallback(() => {
    clearBootstrapSessionCache();
    clearFrontendConfigCache();
    clearFinanceReadCache();
    clearDynamicRoutePermissionContract();
    markPolicySnapshotLoadFailed(false);
    dispatch({ type: "SET_AUTHENTICATED", payload: false });
    dispatch({ type: "SET_USER_NAME", payload: "" });
    dispatch({ type: "SET_USER_ROLE", payload: "guest" });
    dispatch({ type: "SET_PERMISSIONS", payload: [] });
    dispatch({ type: "SET_SERVICES", payload: [] });
    dispatch({ type: "SET_SERVICE_ITEMS", payload: [] });
    dispatch({ type: "SET_POLICY_SNAPSHOT_READY", payload: false });
    dispatch({ type: "SET_POLICY_SNAPSHOT_ERROR", payload: false });
    dispatch({ type: "SET_POLICY_META", payload: null });
    dispatch({ type: "RESET_FAILED_ATTEMPTS" });
    // Single session teardown: gateway cookie logout + hard redirect to /login.
    invalidateSession({ redirectTo: "/login" });
  }, []);

  // Activity monitoring
  useEffect(() => {
    const updateActivity = () => {
      dispatch({ type: "UPDATE_LAST_ACTIVITY" });
    };

    const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart"];
    events.forEach((event) => {
      document.addEventListener(event, updateActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, []);

  // Auto-logout after inactivity
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const checkInactivity = () => {
      const now = new Date();
      const timeDiff = now.getTime() - state.lastActivity.getTime();
      const inactiveThreshold = 30 * 60 * 1000; // 30 minutes

      if (timeDiff > inactiveThreshold) {
        logout();
        addNotification({
          title: t("auth.sessionExpiredTitle"),
          message: t("auth.sessionExpiredMessage"),
          type: "warning",
          read: false,
        });
      }
    };

    const interval = setInterval(checkInactivity, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [state.isAuthenticated, state.lastActivity, addNotification, logout, t]);

  // Failed attempts monitoring
  useEffect(() => {
    if (state.failedAttempts >= 5) {
      dispatch({ type: "SET_LOCKED", payload: true });
      addNotification({
        title: t("auth.accountLockedTitle"),
        message: t("auth.accountLockedMessage"),
        type: "error",
        read: false,
      });

      // Auto-unlock after 15 minutes
      const timeoutId = window.setTimeout(
        () => {
          dispatch({ type: "SET_LOCKED", payload: false });
          dispatch({ type: "RESET_FAILED_ATTEMPTS" });
          addNotification({
            title: t("auth.accountUnlockedTitle"),
            message: t("auth.accountUnlockedMessage"),
            type: "info",
            read: false,
          });
        },
        15 * 60 * 1000
      );

      return () => window.clearTimeout(timeoutId);
    }
    return undefined;
  }, [state.failedAttempts, addNotification, t]);

  const failPolicySnapshot = useCallback(() => {
    clearPolicySnapshotPermissions();
    clearDynamicRoutePermissionContract();
    markPolicySnapshotLoadFailed(true);
    dispatch({ type: "SET_PERMISSIONS", payload: [] });
    dispatch({ type: "SET_SERVICES", payload: [] });
    dispatch({ type: "SET_SERVICE_ITEMS", payload: [] });
    dispatch({ type: "SET_POLICY_META", payload: null });
    dispatch({ type: "SET_POLICY_SNAPSHOT_READY", payload: false });
    dispatch({ type: "SET_POLICY_SNAPSHOT_ERROR", payload: true });
  }, []);

  const commitPolicySnapshot = useCallback((snapshot: PlatformPolicySnapshot) => {
    const permissions = normalizedPermissionsFromSnapshot(snapshot);
    const services = normalizedServicesFromSnapshot(snapshot);
    const serviceItems = normalizedServiceItemsFromSnapshot(snapshot);
    const policyMeta =
      snapshot.meta && typeof snapshot.meta === "object" && !Array.isArray(snapshot.meta)
        ? (snapshot.meta as Record<string, unknown>)
        : null;
    setPolicySnapshotPermissions(permissions);
    setDynamicRoutePermissionContract(serviceItemsRouteContractEntries(serviceItems));
    markPolicySnapshotLoadFailed(false);
    dispatch({ type: "SET_PERMISSIONS", payload: permissions });
    dispatch({ type: "SET_SERVICES", payload: services });
    dispatch({ type: "SET_SERVICE_ITEMS", payload: serviceItems });
    dispatch({ type: "SET_POLICY_META", payload: policyMeta });
    dispatch({ type: "SET_POLICY_SNAPSHOT_READY", payload: true });
    dispatch({ type: "SET_POLICY_SNAPSHOT_ERROR", payload: false });
  }, []);

  const refreshPolicySnapshot = useCallback(async (options?: {
    swr?: boolean;
    force?: boolean;
    seedPermissions?: readonly string[];
    retryAttempted?: boolean;
  }): Promise<boolean> => {
    const seedPermissions = Array.isArray(options?.seedPermissions)
      ? normalizedPermissionsFromSnapshot({ permissions: [...options.seedPermissions] })
      : null;
    if (seedPermissions !== null) {
      setPolicySnapshotPermissions(seedPermissions);
      dispatch({ type: "SET_PERMISSIONS", payload: seedPermissions });
      dispatch({ type: "SET_POLICY_SNAPSHOT_READY", payload: false });
      dispatch({ type: "SET_POLICY_SNAPSHOT_ERROR", payload: false });
    }
    const forceNetwork = options?.force === true || options?.swr === false;
    const loadNavSnapshot = async (): Promise<"committed" | "failed"> => {
      try {
        const response = await governanceApiService.getGatewayResourceItems({
          scope: "nav",
          force: forceNetwork,
          swr: forceNetwork ? false : options?.swr,
        });
        if (!response.success) {
          return "failed";
        }
        if (!isPolicySnapshotData(response.data)) {
          return "failed";
        }
        commitPolicySnapshot(response.data);
        return "committed";
      } catch {
        return "failed";
      }
    };
    const first = await loadNavSnapshot();
    if (first === "committed") {
      return true;
    }
    if (options?.retryAttempted === true) {
      failPolicySnapshot();
      return false;
    }
    clearSharedReadCache("authz");
    const retry = await refreshPolicySnapshot({
      force: true,
      swr: false,
      retryAttempted: true,
    });
    return retry;
  }, [commitPolicySnapshot, failPolicySnapshot]);

  const applyAuthProfile = useCallback((profile: AuthProfileSnapshot) => {
    const user = profile.user && typeof profile.user === "object" ? profile.user : {};
    const username = firstText([profile.username, user.username, profile.user_id, user.id]);
    const role = firstText([profile.role, "member"]);
    dispatch({ type: "SET_AUTHENTICATED", payload: true });
    dispatch({ type: "SET_USER_NAME", payload: username });
    dispatch({ type: "SET_USER_ROLE", payload: role });
    dispatch({ type: "UPDATE_LAST_ACTIVITY" });
  }, []);

  const clearAuthState = useCallback(() => {
    clearBootstrapSessionCache();
    clearFrontendConfigCache();
    clearPolicySnapshotPermissions();
    clearDynamicRoutePermissionContract();
    dispatch({ type: "SET_AUTHENTICATED", payload: false });
    dispatch({ type: "SET_USER_NAME", payload: "" });
    dispatch({ type: "SET_USER_ROLE", payload: "guest" });
    dispatch({ type: "SET_PERMISSIONS", payload: [] });
    dispatch({ type: "SET_SERVICES", payload: [] });
    dispatch({ type: "SET_SERVICE_ITEMS", payload: [] });
    dispatch({ type: "SET_POLICY_SNAPSHOT_READY", payload: false });
    dispatch({ type: "SET_POLICY_SNAPSHOT_ERROR", payload: false });
    dispatch({ type: "SET_POLICY_META", payload: null });
  }, []);

  const hydrateSessionFromGateway = useCallback(async (options?: {
    revalidateConfig?: boolean;
    deferPolicy?: boolean;
    /** Retry /me briefly after login so Set-Cookie is visible before we fail the session. */
    authRetry?: boolean;
  }): Promise<boolean> => {
    const revalidateConfig = options?.revalidateConfig === true;
    const deferPolicy = options?.deferPolicy !== false;
    const authRetry = options?.authRetry === true;
    const epoch = ++hydrateEpochRef.current;
    const isCurrent = () => hydrateEpochRef.current === epoch;
    if (isSessionInvalidationInProgress()) {
      if (isCurrent()) {
        dispatch({ type: "SET_SESSION_HYDRATED", payload: true });
      }
      return false;
    }
    try {
      const baseUrl = apiService.getBaseURL();
      const meShellStorageKey = BOOTSTRAP_AUTH_ME_SHELL_KEY;
      const configShellStorageKey = BOOTSTRAP_CONFIG_SHELL_KEY;
      const bootstrapTtlMs = 5 * 60 * 1000;

      const readCachedMeShell = (): AuthProfileSnapshot | null => {
        try {
          const raw = sessionStorage.getItem(meShellStorageKey);
          if (raw === null) return null;
          const stored = JSON.parse(raw) as { storedAt?: number; profile?: AuthProfileSnapshot };
          if (typeof stored.storedAt !== "number" || Date.now() - stored.storedAt >= bootstrapTtlMs) {
            sessionStorage.removeItem(meShellStorageKey);
            return null;
          }
          if (!stored.profile || typeof stored.profile !== "object") return null;
          return stored.profile;
        } catch {
          return null;
        }
      };

      const writeCachedMeShell = (profile: AuthProfileSnapshot) => {
        try {
          sessionStorage.setItem(meShellStorageKey, JSON.stringify({ storedAt: Date.now(), profile }));
        } catch {
          /* private mode or quota */
        }
      };

      const readCachedConfigShell = (): FrontendConfig | null => {
        try {
          const raw = sessionStorage.getItem(configShellStorageKey);
          if (raw === null) return null;
          const stored = JSON.parse(raw) as { storedAt?: number; config?: FrontendConfig };
          if (typeof stored.storedAt !== "number" || Date.now() - stored.storedAt >= bootstrapTtlMs) {
            sessionStorage.removeItem(configShellStorageKey);
            return null;
          }
          if (!stored.config || !Array.isArray(stored.config.navigation_items)) return null;
          return stored.config;
        } catch {
          return null;
        }
      };

      const writeCachedConfigShell = (config: FrontendConfig) => {
        try {
          sessionStorage.setItem(configShellStorageKey, JSON.stringify({ storedAt: Date.now(), config }));
        } catch {
          /* private mode or quota */
        }
      };

      const readMe = async (): Promise<Response> =>
        fetch(`${baseUrl}/v1/auth/me?view=shell`, {
          method: "GET",
          credentials: authFetchCredentials(),
          headers: { Accept: "application/json" },
        });

      const loadAuthProfile = async (): Promise<AuthProfileSnapshot | null> => {
        const attemptMe = async (): Promise<Response> => {
          let response = await readMe();
          if (response.status === 401) {
            const refreshed = await refreshSessionTokens();
            if (refreshed) {
              response = await readMe();
            }
          }
          return response;
        };

        let response = await attemptMe();
        if (authRetry && response.status === 401) {
          for (const delayMs of [50, 150, 300]) {
            if (!isCurrent()) {
              return null;
            }
            await new Promise((resolve) => window.setTimeout(resolve, delayMs));
            response = await attemptMe();
            if (response.status !== 401) {
              break;
            }
          }
        }
        if (!isCurrent()) {
          return null;
        }
        if (response.status === 401) {
          clearBootstrapSessionCache();
          if (isCurrent()) {
            clearAuthState();
          }
          return null;
        }
        if (!response.ok) {
          if (isCurrent()) {
            markPolicySnapshotLoadFailed(true);
          }
          return readCachedMeShell();
        }
        const body = (await response.json()) as { data?: AuthProfileSnapshot };
        if (!body.data || typeof body.data !== "object") {
          if (isCurrent()) {
            clearAuthState();
          }
          return null;
        }
        if (isCurrent()) {
          writeCachedMeShell(body.data);
        }
        return body.data;
      };

      const cachedProfile =
        typeof window !== "undefined" &&
        window.location.pathname !== "/login" &&
        !window.location.pathname.startsWith("/login/")
          ? readCachedMeShell()
          : null;
      const cachedConfig = revalidateConfig ? null : readCachedConfigShell();
      if (cachedConfig !== null && isCurrent()) {
        seedFrontendConfigCache(cachedConfig);
      }
      // Optimistic shell paint only — always revalidate gateway cookies before trusting auth.
      if (cachedProfile !== null && isCurrent()) {
        applyAuthProfile(cachedProfile);
      }

      const authPromise = loadAuthProfile();
      const configPromise =
        cachedConfig !== null
          ? Promise.resolve({
              success: true,
              data: cachedConfig,
            })
          : apiService.getFrontendConfig({ view: "shell" });
      const [profile, configResponse] = await Promise.all([authPromise, configPromise]);
      if (!isCurrent()) {
        // A newer hydrate owns the UI; cookies/session may already be valid.
        return profile !== null || isAuthenticatedRef.current;
      }
      if (profile === null) {
        if (!isAuthenticatedRef.current) {
          clearAuthState();
        }
        dispatch({ type: "SET_SESSION_HYDRATED", payload: true });
        return isAuthenticatedRef.current;
      }
      applyAuthProfile(profile);
      applyFrontendConfigFromResponse(configResponse);
      if (configResponse.success && configResponse.data && typeof configResponse.data === "object") {
        const config = configResponse.data as FrontendConfig;
        if (Array.isArray(config.navigation_items)) {
          writeCachedConfigShell(config);
        }
      } else if (revalidateConfig) {
        logFrontendConfigWarmFailure(configResponse);
      }
      dispatch({ type: "SET_SESSION_HYDRATED", payload: true });
      if (!deferPolicy) {
        if (isCurrent()) {
          await refreshPolicySnapshot({ swr: false });
        }
        // Session shell is prepared even if a newer hydrate superseded this epoch
        // while policy was loading — do not surface that as login failure.
        return true;
      }
      // After list chrome paints — avoids gateway/DB contention on TTFI-critical reads.
      // Cap wait: permission-gated lists cannot paint until policy resolves.
      const coldStartedAt = Date.now();
      const coldSchedulePolicy = () => {
        if (!isCurrent()) {
          return;
        }
        const painted = document.querySelector(
          'table tbody tr, [data-testid="consultation-ready"], [data-testid="consultation-queue-row"], [data-testid*="-ready"]',
        );
        if (painted || Date.now() - coldStartedAt > 300) {
          void refreshPolicySnapshot({ swr: false });
          return;
        }
        window.setTimeout(coldSchedulePolicy, 50);
      };
      window.setTimeout(coldSchedulePolicy, 50);
      return true;
    } catch {
      if (isCurrent()) {
        if (!isAuthenticatedRef.current) {
          clearAuthState();
        }
        markPolicySnapshotLoadFailed(true);
        dispatch({ type: "SET_SESSION_HYDRATED", payload: true });
      }
      return isAuthenticatedRef.current;
    }
  }, [applyAuthProfile, clearAuthState, refreshPolicySnapshot]);

  const setAuthFromKeycloak = useCallback((_accessToken: string) => {
    publishSessionChanged();
  }, []);

  const login = useCallback(
    async (username: string, password: string): Promise<boolean> => {
      if (isKeycloakMode()) {
        try {
          const redirectUri = typeof window !== "undefined" ? `${window.location.origin}/callback` : "";
          const { url } = await buildKeycloakAuthUrl(redirectUri);
          window.location.href = url;
          return true;
        } catch {
          addNotification({
            title: t("auth.loginErrorTitle"),
            message: t("auth.loginKeycloakRedirectFailed"),
            type: "error",
            read: false,
          });
          return false;
        }
      }
      if (state.isLocked) {
        addNotification({
          title: t("auth.loginBlockedTitle"),
          message: t("auth.loginBlockedMessage"),
          type: "error",
          read: false,
        });
        return false;
      }

      try {
        const response = await apiService.post("/v1/auth/login", {
          username,
          password,
        });
        if (response.success) {
          interface LoginResponse {
            role?: string;
            permissions?: string[];
            name?: string;
            user?: { username?: string; email?: string };
          }
          const userData = response.data ? (response.data as LoginResponse) : {};
          const user = userData.user && typeof userData.user === "object" ? userData.user : {};
          const displayName = firstText([user.username, userData.name, username]);
          clearFinanceReadCache();
          clearSharedReadCache("authz");
          // Drop pre-auth / stale shell so Sidebar cannot stick on an empty 15-minute React Query cache.
          clearFrontendConfigCache();
          try {
            sessionStorage.removeItem(BOOTSTRAP_CONFIG_SHELL_KEY);
          } catch {
            /* private mode or quota */
          }

          // Cancel any in-flight 401 logout so it cannot clear the cookies we just received.
          acknowledgeAuthenticatedSession();
          const hydrated = await hydrateSessionFromGateway({
            revalidateConfig: true,
            deferPolicy: false,
            authRetry: true,
          });
          if (!hydrated) {
            addNotification({
              title: t("auth.loginFailedTitle"),
              message: t("auth.loginSessionPrepareFailed"),
              type: "error",
              read: false,
            });
            return false;
          }

          // Mark nav config stale so the first Sidebar mount refetches if seed was skipped,
          // while still keeping any seeded shell as placeholder data.
          await getPlatformQueryClient().invalidateQueries({
            queryKey: tenantScopedQueryKey(readActiveTenantQueryScope(), FRONTEND_CONFIG_QUERY_KEY),
          });

          dispatch({ type: "RESET_FAILED_ATTEMPTS" });
          dispatch({ type: "UPDATE_LAST_ACTIVITY" });

          addNotification({
            title: t("auth.loginSuccessTitle"),
            message: t("auth.loginSuccessMessage", { name: displayName }),
            type: "success",
            read: false,
          });

          return true;
        } else {
          dispatch({ type: "INCREMENT_FAILED_ATTEMPTS" });
          let message = t("auth.loginInvalidCredentials", { remaining: 5 - state.failedAttempts });
          if (response.error) {
            message = response.error;
          }
          addNotification({
            title: t("auth.loginFailedTitle"),
            message,
            type: "error",
            read: false,
          });

          return false;
        }
      } catch (error) {
        addNotification({
          title: t("auth.loginErrorTitle"),
          message: t("auth.loginErrorMessage"),
          type: "error",
          read: false,
        });
        return false;
      }
    },
    [state.isLocked, state.failedAttempts, addNotification, hydrateSessionFromGateway, t]
  );

  useEffect(() => {
    void hydrateSessionFromGateway();
    const onSessionChanged = () => {
      if (isSessionInvalidationInProgress()) {
        return;
      }
      void hydrateSessionFromGateway();
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === "9s_session_event") {
        if (isSessionInvalidationInProgress()) {
          return;
        }
        void hydrateSessionFromGateway();
      }
    };
    window.addEventListener(SESSION_CHANGED_EVENT, onSessionChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(SESSION_CHANGED_EVENT, onSessionChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [hydrateSessionFromGateway]);

  const checkPermission = useCallback(
    (permission: string): boolean => hasPermission(permission as HubPermission),
    []
  );

  const touchSessionActivity = useCallback(() => {
    dispatch({ type: "UPDATE_LAST_ACTIVITY" });
  }, []);

  const updateSecurityLevel = useCallback(
    (level: "low" | "medium" | "high") => {
      dispatch({ type: "SET_SECURITY_LEVEL", payload: level });
      addNotification({
        title: t("auth.securityLevelUpdatedTitle"),
        message: t("auth.securityLevelUpdatedMessage", { level }),
        type: "info",
        read: false,
      });
    },
    [addNotification, t]
  );

  const validateInput = useCallback((input: string): string => {
    return securityMiddleware.sanitizeInput(input);
  }, []);

  const generateCSRFToken = useCallback((): string => {
    return securityMiddleware.generateSecureToken();
  }, []);

  const validateCSRFToken = useCallback((token: string): boolean => {
    return securityMiddleware.validateCSRFToken(token, "session-token");
  }, []);

  const value: SecurityContextType = {
    state,
    dispatch,
    login,
    logout,
    setAuthFromKeycloak,
    refreshPolicySnapshot,
    checkPermission,
    touchSessionActivity,
    updateSecurityLevel,
    validateInput,
    generateCSRFToken,
    validateCSRFToken,
  };

  return <SecurityContext.Provider value={value}>{children}</SecurityContext.Provider>;
};
